import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Alert,
 // Button,
  Snackbar,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/common/PageHeader';
import DataTable, { Column } from '@/components/common/DataTable';
import { licensesApi, Product } from '@/api/licenses-api';
//import { sherwebApi } from '@/api/sherweb-api';

export default function ProductsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [syncInProgress, setSyncInProgress] = useState(false);

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await licensesApi.getProducts();
      setProducts(data);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Failed to load products. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleSyncProducts = async () => {
    if (user?.role !== 'distributor') return;
    
    setSyncInProgress(true);
    try {
      // In a real implementation, this would call a function to sync with Sherweb
      // For now, let's simulate by refreshing the product list
      await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate sync delay
      await fetchProducts();
      setSuccessMessage('Products synchronized successfully');
    } catch (err) {
      console.error('Error syncing products:', err);
      setError('Failed to synchronize products. Please try again.');
    } finally {
      setSyncInProgress(false);
    }
  };

  // Filter products by search term
  const filteredProducts = products.filter(product => 
    product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.sherwebSku.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.chargebeeItemPriceId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns: Column<Product>[] = [
    {
      id: 'sherwebSku',
      label: 'Sherweb SKU',
      minWidth: 120,
    },
    {
      id: 'description',
      label: 'Description',
      minWidth: 250,
    },
    {
      id: 'chargebeeItemPriceId',
      label: 'Chargebee Item Price ID',
      minWidth: 180,
    },
    {
      id: 'metadata',
      label: 'Price',
      minWidth: 120,
      format: (value) => {
        return value && value.price ? `$${value.price.toFixed(2)}` : 'N/A';
      },
    },
    {
      id: 'metadata',
      label: 'Status',
      minWidth: 120,
      format: (value) => {
        if (!value || value.status === undefined) return 'N/A';
        return value.status === 'active' ? (
          <Chip label="Active" color="success" size="small" />
        ) : (
          <Chip label="Inactive" color="default" size="small" />
        );
      },
    },
    {
      id: 'createdAt',
      label: 'Added Date',
      minWidth: 120,
      format: (value) => new Date(value).toLocaleDateString(),
    },
  ];

  return (
    <>
      <PageHeader
        title="Products Catalog"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Products' },
        ]}
        action={
          user?.role === 'distributor' ? {
            label: 'Sync Catalog',
            onClick: handleSyncProducts,
            icon: <RefreshIcon />,
          } : undefined
        }
      />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Product Catalog</Typography>
            
            <TextField
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
              sx={{ width: 250 }}
            />
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {syncInProgress && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Synchronizing product catalog... This may take a moment.
        </Alert>
      )}

      <DataTable
        columns={columns}
        data={filteredProducts}
        loading={loading || syncInProgress}
        getRowId={(row) => row.productId}
        onRowClick={(row) => navigate(`/products/${row.productId}`)}
        emptyMessage={
          searchTerm
            ? "No products match your search criteria."
            : "No products found in the catalog."
        }
      />

      {/* Success notification */}
      <Snackbar 
        open={!!successMessage} 
        autoHideDuration={6000} 
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
